import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  apiGetProject,
  apiGetProjectChats,
  apiUpdateProject,
  apiDeleteProject,
  apiRemoveChatFromProject,
} from '~/lib/api/projects';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import ShareDialog from '~/components/projects/ShareDialog';
import { $activeProjectId } from '~/lib/stores/projects';
import type { Project } from '@shared/types/project';
import type { ChatListItem } from '@shared/types/chat';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descInput, setDescInput] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  const { data: project, isLoading: projectLoading } = useQuery<Project>({
    queryKey: ['project', id],
    queryFn: () => apiGetProject(id!),
    enabled: !!id,
  });

  const { data: chats = [] } = useQuery<ChatListItem[]>({
    queryKey: ['project-chats', id],
    queryFn: () => apiGetProjectChats(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string }) => apiUpdateProject(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDeleteProject(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted');
      navigate('/projects');
    },
    onError: () => {
      toast.error("Couldn't delete that project.");
    },
  });

  const removeChatMutation = useMutation({
    mutationFn: (chatId: string) => apiRemoveChatFromProject(id!, chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-chats', id] });
      queryClient.invalidateQueries({ queryKey: ['project', id] });
      toast.success('Chat removed from project');
    },
  });

  function startEditName() {
    if (project) {
      setNameInput(project.name);
      setEditingName(true);
    }
  }

  function saveName() {
    if (nameInput.trim() && nameInput.trim() !== project?.name) {
      updateMutation.mutate({ name: nameInput.trim() });
    }

    setEditingName(false);
  }

  function startEditDescription() {
    if (project) {
      setDescInput(project.description || '');
      setEditingDescription(true);
    }
  }

  function saveDescription() {
    updateMutation.mutate({ description: descInput.trim() });
    setEditingDescription(false);
  }

  // Removing a chat is one tap from its link, so confirm first (easy to mis-tap on a phone).
  const [removeTarget, setRemoveTarget] = useState<{ id: string; label: string } | null>(null);

  function handleStartNewChat() {
    $activeProjectId.set(id);
    navigate('/');
  }

  if (projectLoading) {
    return (
      <div className="min-h-dvh bg-page flex items-center justify-center">
        <p className="text-secondary">Loading project…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-dvh bg-page flex items-center justify-center">
        <p className="text-secondary">Project not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-page py-8 sm:py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/projects" className="text-sm text-secondary hover:text-primary transition-colors">
            &larr; All Projects
          </Link>
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-8">
          <div className="flex-1 min-w-0">
            {editingName ? (
              <input
                type="text"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveName();
                  } else if (e.key === 'Escape') {
                    setEditingName(false);
                  }
                }}
                className="text-2xl font-bold bg-transparent border-b-2 border-accent text-primary focus:outline-none w-full"
                autoFocus
              />
            ) : (
              <div className="group flex items-center gap-2">
                <h1
                  onClick={startEditName}
                  className="min-w-0 break-words text-2xl font-bold text-primary cursor-pointer hover:text-accent transition-colors"
                >
                  {project.name}
                </h1>
                <button
                  type="button"
                  onClick={startEditName}
                  aria-label="Rename project"
                  title="Rename project"
                  className="ui-btn-icon shrink-0 opacity-0 group-hover:opacity-60 hover:!opacity-100 focus:opacity-100 touch:opacity-60 transition-opacity"
                >
                  <span className="i-ph:pencil-simple text-base" />
                </button>
                {project.isPublic && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400">
                    Public
                  </span>
                )}
              </div>
            )}

            {editingDescription ? (
              <textarea
                value={descInput}
                onChange={(e) => setDescInput(e.target.value)}
                onBlur={saveDescription}
                rows={2}
                className="mt-2 w-full text-sm bg-transparent border-b-2 border-accent text-secondary focus:outline-none resize-none"
                autoFocus
              />
            ) : (
              <p
                onClick={startEditDescription}
                className="mt-2 text-sm text-secondary cursor-pointer hover:text-primary transition-colors"
              >
                {project.description || 'Click to add a description...'}
              </p>
            )}

            {project.forkedFrom && (
              <p className="mt-1.5 text-xs text-muted">
                <span className="i-ph:git-fork inline-block align-text-bottom mr-0.5" />
                Forked from{' '}
                <Link
                  to={`/shared/project/${project.forkedFrom}`}
                  className="text-accent hover:text-accent transition-colors"
                >
                  original project
                </Link>
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:ml-4">
            <button onClick={handleStartNewChat} className="ui-btn-accent">
              <span className="i-ph:plus text-base" />
              New Chat
            </button>
            <button
              onClick={() => setShowShareDialog(true)}
              aria-label="Share"
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-hairline text-secondary hover:text-primary hover:border-accent/50 transition-colors touch:min-h-10"
            >
              <span className="i-ph:share-network text-base" />
              <span className="hidden sm:inline">Share</span>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              aria-label="Delete project"
              className="p-2 rounded-md text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors touch:p-2.5"
            >
              <span className="i-ph:trash text-lg" />
            </button>
          </div>
        </div>

        {/* Chats list */}
        <div>
          <h2 className="text-lg font-semibold text-primary mb-4">Chats ({chats.length})</h2>

          {chats.length === 0 ? (
            <div className="text-center py-12 rounded-lg border border-hairline bg-surface">
              <div className="i-ph:chat-circle-dots text-4xl text-muted mx-auto mb-3" />
              <p className="text-secondary mb-3">No chats in this project yet</p>
              <button
                onClick={handleStartNewChat}
                className="text-accent hover:text-accent font-medium transition-colors"
              >
                Start a new chat
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {chats.map((chat) => (
                <div
                  key={chat.id}
                  className="flex items-center justify-between rounded-lg border border-hairline bg-surface px-4 py-3 hover:border-accent/50 transition-colors"
                >
                  <Link
                    to={`/chat/${chat.id}`}
                    className="flex-1 min-w-0 text-sm text-primary hover:text-accent truncate transition-colors"
                  >
                    {chat.description || 'Untitled chat'}
                  </Link>
                  <div className="flex shrink-0 items-center gap-2 ml-3 sm:gap-3 sm:ml-4">
                    <span className="text-xs text-muted">{new Date(chat.updatedAt).toLocaleDateString()}</span>
                    <button
                      onClick={() => setRemoveTarget({ id: chat.id, label: chat.description || 'Untitled chat' })}
                      className="flex h-8 w-8 items-center justify-center rounded text-muted hover:text-red-500 transition-colors touch:h-10 touch:w-10"
                      title="Remove from project"
                      aria-label="Remove from project"
                    >
                      <span className="i-ph:x text-sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete this project?"
        description={`Are you sure you want to delete "${project.name}"? Chats will be unlinked but not deleted.`}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) {
            removeChatMutation.mutate(removeTarget.id);
          }

          setRemoveTarget(null);
        }}
        title="Remove this chat from the project?"
        description={`"${removeTarget?.label}" will be unlinked from this project. The chat itself is not deleted.`}
        confirmLabel="Remove"
        variant="destructive"
      />

      <ShareDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        project={project}
        onProjectUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ['project', id] });
          queryClient.invalidateQueries({ queryKey: ['projects'] });
        }}
      />
    </div>
  );
}
